import { z } from "zod";
import {
  formatSettingsSchema,
  validationSettingsSchema,
} from "./field-policies";
import { uid, safePattern, type Field } from "./schema";

export const variableDataTypes = [
  "text",
  "textarea",
  "phone",
  "email",
  "zip",
  "uri",
  "number",
  "date",
  "datetime",
  "time",
  "color",
] as const;
export const variableTypeMappingSchema = z
  .object({
    aliases: z.array(z.string().trim().min(1).max(100)).min(1).max(100),
    dataType: z.enum(variableDataTypes),
    match: z.enum(["exact", "words"]).default("words"),
    enabled: z.boolean().default(true),
  })
  .strict();

const id = z.string().min(1).max(200);
export const fieldPresetKindSchema = z.enum([
  "validation",
  "format",
  "variable-type",
]);
export const fieldPresetScopeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("application") }).strict(),
  z.object({ kind: z.literal("site"), siteId: id }).strict(),
  z.object({ kind: z.literal("company"), siteId: id, companyId: id }).strict(),
  z
    .object({
      kind: z.literal("project"),
      siteId: id,
      companyId: id,
      projectId: id,
    })
    .strict(),
  z
    .object({
      kind: z.literal("variable"),
      siteId: id,
      companyId: id,
      projectId: id,
      fieldId: id,
    })
    .strict(),
]);
export type FieldPresetScope = z.infer<typeof fieldPresetScopeSchema>;
export const fieldPresetSchema = z
  .object({
    id,
    key: id,
    name: id,
    kind: fieldPresetKindSchema,
    version: z.number().int().positive(),
    scope: fieldPresetScopeSchema,
    value: z.unknown(),
    archived: z.boolean().optional(),
  })
  .strict()
  .transform((p, ctx) => {
    const parsed = (
      p.kind === "variable-type"
        ? variableTypeMappingSchema
        : p.kind === "validation"
          ? validationSettingsSchema
          : formatSettingsSchema
    ).safeParse(p.value);
    if (!parsed.success) {
      for (const issue of parsed.error.issues)
        ctx.addIssue({ ...issue, path: ["value", ...issue.path] });
      return z.NEVER;
    }
    if (
      p.kind === "validation" &&
      "pattern" in parsed.data &&
      parsed.data.pattern
    ) {
      try {
        safePattern(parsed.data.pattern);
      } catch (error) {
        ctx.addIssue({
          code: "custom",
          message: (error as Error).message,
          path: ["value", "pattern"],
        });
        return z.NEVER;
      }
    }
    const value = { ...parsed.data };
    if ("preset" in value) delete value.preset;
    if (
      p.kind === "variable-type" &&
      !["application", "site", "company"].includes(p.scope.kind)
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Variable mappings support global, site and company scopes",
        path: ["scope"],
      });
      return z.NEVER;
    }
    return { ...p, value };
  });
export type FieldPreset = z.infer<typeof fieldPresetSchema>;
export interface FieldPresetContext {
  canManageApplication?: boolean;
  siteId: string;
  companyId: string;
  siteName?: string;
  companyName?: string;
  canManageSite: boolean;
  canManageCompany: boolean;
  canManageProject: boolean;
}
export const fieldPresetQuerySchema = z
  .object({
    kind: fieldPresetKindSchema.optional(),
    projectId: id.optional(),
    fieldId: id.optional(),
  })
  .strict()
  .refine(
    (v) => !v.fieldId || !!v.projectId,
    "Variable scope requires a project",
  );
export type FieldPresetQuery = z.infer<typeof fieldPresetQuerySchema>;
export interface FieldPresetCatalog {
  context: FieldPresetContext;
  presets: FieldPreset[];
}
export const fieldPresetWriteSchema = z
  .object({
    id: id.optional(),
    key: id,
    name: id,
    kind: fieldPresetKindSchema,
    scope: fieldPresetScopeSchema,
    expectedVersion: z.number().int().nonnegative(),
    value: z.unknown(),
    archived: z.boolean().optional(),
  })
  .strict();
export type FieldPresetWrite = z.infer<typeof fieldPresetWriteSchema>;
export class FieldPresetError extends Error {
  constructor(
    message: string,
    readonly status = 400,
  ) {
    super(message);
  }
}
export function builtinFieldPresets(): FieldPreset[] {
  const validation: [string, string, unknown][] = [
    ["automatic", "Automatic by variable type", {}],
    ["none", "No additional validation", { rule: "none" }],
    ["website", "Website URL (HTTP / HTTPS)", { rule: "url" }],
    ["email", "Email address", { rule: "email" }],
    ["phone", "Phone number", { rule: "phone" }],
    ["number", "Decimal number", { rule: "number" }],
    ["integer", "Whole number", { rule: "integer" }],
    ["positive", "Positive number", { rule: "positive" }],
    ["nonnegative", "Zero or positive number", { rule: "nonnegative" }],
    ["zip", "US ZIP / ZIP+4", { rule: "postal-us" }],
    ["postal-canada", "Canadian postal code", { rule: "postal-ca" }],
    ["uuid", "UUID", { rule: "uuid" }],
    ["hex-color", "Hex color", { rule: "hex-color" }],
    ["alphanumeric", "Letters and numbers", { rule: "alphanumeric" }],
    ["short-text", "Short text (100 characters)", { maxLength: 100 }],
    ["long-text", "Long text (2,000 characters)", { maxLength: 2000 }],
  ];
  const formatting: [string, string, unknown][] = [
    ["original", "Original value", {}],
    ["uppercase", "UPPERCASE", { kind: "text", uppercase: true }],
    ["lowercase", "lowercase", { kind: "text", casing: "lowercase" }],
    ["title", "Title Case", { kind: "text", casing: "title" }],
    ["trim", "Trim spaces", { kind: "text", trim: true }],
    ...[
      ["date-iso", "Date · 2024-02-29", "yyyy-MM-dd"],
      ["date-us", "Date · 02/29/2024", "MM/dd/yyyy"],
      ["date-uk", "Date · 29/02/2024", "dd/MM/yyyy"],
      ["date-short", "Date · Feb 29, 2024", "MMM d, yyyy"],
      ["date-long", "Date · February 29, 2024", "MMMM d, yyyy"],
      ["datetime", "Date & time · 2024-02-29 13:05", "yyyy-MM-dd HH:mm"],
      ["time-24", "Time · 13:05", "HH:mm"],
      ["time-seconds", "Time · 13:05:07", "HH:mm:ss"],
      ["time-12", "Time · 1:05 PM", "h:mm a"],
    ].map(
      ([key, name, datePattern]) =>
        [key, name, { kind: "date", datePattern }] as [string, string, unknown],
    ),
    ...[
      ["integer", "Number · 1,235", "#,##0"],
      ["decimal", "Number · 1,234.57", "#,##0.00"],
      ["flexible-decimal", "Number · up to 4 decimals", "#,##0.####"],
      ["percent", "Percent · 12.5%", "0.0%"],
      ["padded", "Number · 000123", "000000"],
    ].map(
      ([key, name, numberPattern]) =>
        [key, name, { kind: "number", numberPattern }] as [
          string,
          string,
          unknown,
        ],
    ),
    ...["USD", "EUR", "GBP", "CAD"].map(
      (currency) =>
        [
          "currency-" + currency.toLowerCase(),
          "Currency · " + currency,
          { kind: "number", numberPattern: "¤#,##0.00", currency },
        ] as [string, string, unknown],
    ),
  ];
  return [
    ...validation.map(([key, name, value]) => ({
      key,
      name,
      value,
      kind: "validation" as const,
    })),
    ...formatting.map(([key, name, value]) => ({
      key,
      name,
      value,
      kind: "format" as const,
    })),
    ...(
      [
        [
          "phone",
          "Phone",
          [
            "phone",
            "phone number",
            "telephone",
            "tel",
            "mobile",
            "cell phone",
            "fax",
          ],
          "phone",
        ],
        [
          "zip",
          "US ZIP code",
          ["zip", "zip code", "zipcode", "zip plus 4"],
          "zip",
        ],
        [
          "uri",
          "Website / URI",
          ["url", "website", "web site", "web address", "uri"],
          "uri",
        ],
        ["email", "Email", ["email", "e mail", "email address"], "email"],
        ["date", "Date", ["date", "birth date", "dob"], "date"],
        [
          "number",
          "Number",
          ["quantity", "qty", "amount", "price", "total"],
          "number",
        ],
        ["color", "Color", ["color", "colour", "hex color"], "color"],
      ] as const
    ).map(([key, name, aliases, dataType]) => ({
      key,
      name,
      value: { aliases: [...aliases], dataType },
      kind: "variable-type" as const,
    })),
  ].map((p) =>
    fieldPresetSchema.parse({
      ...p,
      id: `builtin.${p.kind}.${p.key}`,
      version: 1,
      scope: { kind: "application" },
    }),
  );
}
export function fieldPresetAvailable(
  p: FieldPreset,
  context: FieldPresetContext,
  query: FieldPresetQuery,
) {
  const s = p.scope;
  return (
    s.kind === "application" ||
    (s.siteId === context.siteId &&
      (s.kind === "site" ||
        (s.companyId === context.companyId &&
          (s.kind === "company" ||
            (s.projectId === query.projectId &&
              (s.kind === "project" || s.fieldId === query.fieldId))))))
  );
}
const ranks = { application: 0, site: 1, company: 2, project: 3, variable: 4 };
export function effectiveFieldPresets(rows: FieldPreset[]) {
  const selected = new Map<string, FieldPreset>();
  for (const row of rows.filter((r) => !r.archived)) {
    const key = `${row.kind}:${row.key}`,
      old = selected.get(key);
    if (
      !old ||
      ranks[row.scope.kind] > ranks[old.scope.kind] ||
      (ranks[row.scope.kind] === ranks[old.scope.kind] &&
        row.version >= old.version)
    )
      selected.set(key, row);
  }
  return [...selected.values()];
}
export function applyFieldPreset(field: Field, preset: FieldPreset): Field {
  const p = fieldPresetSchema.parse(preset);
  if (p.archived) throw new FieldPresetError("This preset is archived");
  if (p.kind === "variable-type")
    throw new FieldPresetError(
      "Use the variable type mapper to apply this mapping",
    );
  return {
    ...field,
    [p.kind]: {
      ...p.value,
      preset: {
        id: p.id,
        version: p.version,
        name: p.name,
        scope: p.scope.kind,
      },
    },
  };
}
export interface FieldPresetStore {
  read(): Promise<FieldPreset[]>;
  save(preset: FieldPreset, expectedVersion: number): Promise<void>;
}
export class MemoryFieldPresetStore implements FieldPresetStore {
  protected rows: FieldPreset[] = [];
  protected writes: Promise<unknown> = Promise.resolve();
  async read() {
    await this.writes;
    return structuredClone(this.rows);
  }
  protected async persist(_rows: FieldPreset[]) {}
  async save(raw: FieldPreset, expected: number) {
    const p = fieldPresetSchema.parse(raw);
    const write = this.writes.then(async () => {
      const old = this.rows.filter((r) => r.id === p.id).at(-1);
      if ((old?.version || 0) !== expected || p.version !== expected + 1)
        throw new FieldPresetError("Preset changed; reload before saving", 409);
      if (
        old &&
        (old.kind !== p.kind ||
          old.key !== p.key ||
          JSON.stringify(old.scope) !== JSON.stringify(p.scope))
      )
        throw new FieldPresetError(
          "Preset identity and scope cannot change",
          409,
        );
      const latest = new Map<string, FieldPreset>();
      for (const row of this.rows) latest.set(row.id, row);
      if (
        !p.archived &&
        [...latest.values()].some(
          (row) =>
            row.id !== p.id &&
            !row.archived &&
            row.kind === p.kind &&
            row.key === p.key &&
            JSON.stringify(row.scope) === JSON.stringify(p.scope),
        )
      )
        throw new FieldPresetError(
          "A preset already uses this key in this scope; edit its existing version",
          409,
        );
      const next = [...this.rows, p];
      await this.persist(next);
      this.rows = next;
    });
    this.writes = write.catch(() => {});
    await write;
  }
}
export class FieldPresetService {
  constructor(private store: FieldPresetStore) {}
  async list(
    context: FieldPresetContext,
    raw: FieldPresetQuery = {},
  ): Promise<FieldPresetCatalog> {
    const query = fieldPresetQuerySchema.parse(raw),
      all = [...builtinFieldPresets(), ...(await this.store.read())],
      latest = new Map<string, FieldPreset>();
    for (const row of all)
      if (!latest.has(row.id) || latest.get(row.id)!.version < row.version)
        latest.set(row.id, row);
    return {
      context: structuredClone(context),
      presets: [...latest.values()].filter(
        (r) =>
          (!query.kind || r.kind === query.kind) &&
          fieldPresetAvailable(r, context, query),
      ),
    };
  }
  async save(context: FieldPresetContext, raw: FieldPresetWrite) {
    const input = fieldPresetWriteSchema.parse(raw),
      s = input.scope;
    if (
      (s.kind === "application" &&
        (input.kind !== "variable-type" || !context.canManageApplication)) ||
      input.id?.startsWith("builtin.")
    )
      throw new FieldPresetError(
        "Application presets are read only; create a scoped copy",
        403,
      );
    const query = {
      ...("projectId" in s ? { projectId: s.projectId } : {}),
      ...("fieldId" in s ? { fieldId: s.fieldId } : {}),
    };
    const { expectedVersion, ...definition } = input;
    const next = fieldPresetSchema.parse({
      ...definition,
      id: input.id || uid("field_preset"),
      version: expectedVersion + 1,
    });
    if (
      !fieldPresetAvailable(next, context, query) ||
      !(s.kind === "application"
        ? context.canManageApplication
        : s.kind === "site"
          ? context.canManageSite
          : s.kind === "company"
            ? context.canManageCompany
            : context.canManageProject)
    )
      throw new FieldPresetError(
        "Preset scope is not available for editing",
        403,
      );
    const old = (await this.store.read()).find((r) => r.id === next.id);
    if (old && !fieldPresetAvailable(old, context, query))
      throw new FieldPresetError("Preset is unavailable in this context", 403);
    await this.store.save(next, input.expectedVersion);
    return next;
  }
}
